import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../../lib/supabase.js';
import { useAuth } from '../../lib/auth.js';
import { routes } from '../../routes.js';
import { Button, FormField, TextInput } from '../../components/ui/index.js';
import { ScopeWordmark } from '../../components/ScopeWordmark.js';

interface LoginScreenProps {
  variant?: 'agency' | 'portal';
}

const loginSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

/**
 * Login for both agency users and client contacts. Submits to Supabase Auth;
 * AuthProvider picks up the session and the router redirects based on
 * scope_role metadata.
 */
export function LoginScreen({ variant = 'agency' }: LoginScreenProps) {
  const navigate = useNavigate();
  const { auth, recovery } = useAuth();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({ resolver: zodResolver(loginSchema) });

  // If already signed in, bounce. A password-recovery session must NOT be
  // dropped into the app — send it to the reset screen until a new password
  // is set, matching the route guards in App.tsx.
  useEffect(() => {
    if (recovery) {
      navigate(auth.kind === 'contact' ? routes.portalResetPassword : routes.resetPassword, {
        replace: true,
      });
      return;
    }
    if (auth.kind === 'agency') navigate(routes.dashboard, { replace: true });
    else if (auth.kind === 'contact') navigate(routes.portalDashboard, { replace: true });
  }, [auth.kind, recovery, navigate]);

  const onSubmit = async (values: LoginFormValues) => {
    setSubmitError(null);
    const { error } = await supabase.auth.signInWithPassword(values);
    if (error) {
      setSubmitError(error.message);
      return;
    }
    // AuthProvider's onAuthStateChange will update; routing reacts.
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4 font-sans">
      <div className="w-full max-w-md">
        <div className="mb-10 flex justify-center">
          <ScopeWordmark />
        </div>

        <div className="rounded-lg border border-scope-border bg-white p-8 shadow-scope">
          <h1 className="mb-1 text-2xl font-semibold tracking-tight text-scope-navy">
            {variant === 'portal' ? 'Client portal sign in' : 'Sign in to Scope'}
          </h1>
          <p className="mb-6 text-sm text-scope-text-muted">
            {variant === 'portal'
              ? 'Enter the email your agency invited.'
              : 'Enter your work email to continue.'}
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <FormField label="Email" htmlFor="email" error={errors.email?.message}>
              <TextInput
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@agency.com"
                {...register('email')}
              />
            </FormField>

            <FormField label="Password" htmlFor="password" error={errors.password?.message}>
              <TextInput
                id="password"
                type="password"
                autoComplete="current-password"
                {...register('password')}
              />
            </FormField>

            {submitError && (
              <div className="rounded-lg border border-status-red bg-status-red-bg px-3 py-2 text-xs text-status-red">
                {submitError}
              </div>
            )}

            <Button type="submit" className="w-full" loading={isSubmitting}>
              Sign in
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-scope-text-muted">
            <a
              href={variant === 'portal' ? routes.portalForgotPassword : routes.forgotPassword}
              className="hover:text-scope-navy"
            >
              Forgot password?
            </a>
          </div>
        </div>

        <p className="mt-8 text-center text-xs text-scope-text-muted">
          Scope &middot; The professional services engagement workspace
        </p>
      </div>
    </div>
  );
}
